import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgRole } from '@finvox/shared/org';
import type { MemberPermissionOverrides } from '@finvox/shared/member-access';
import { MemberPermissionsEditor } from '@/components/settings/MemberPermissionsEditor';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { isValidEmail } from '@/lib/emailValidation';

const MEMBER_ROLES = ['accountant', 'bookkeeper', 'viewer'] as const satisfies readonly OrgRole[];

type Props = {
  open: boolean;
  busy: boolean;
  online: boolean;
  onClose: () => void;
  onSave: (payload: {
    email: string;
    password: string;
    role: OrgRole;
    permissions: MemberPermissionOverrides;
  }) => void | Promise<void>;
};

function roleHintKey(role: OrgRole): string {
  return `settings.roles.${role}Hint`;
}

export function AddUserDialog({ open, busy, online, onClose, onSave }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<OrgRole>('bookkeeper');
  const [permissions, setPermissions] = useState<MemberPermissionOverrides>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setRole('bookkeeper');
    setPermissions({});
    setFormError(null);
  }, [open]);

  if (!open) return null;

  async function handleSave() {
    if (!isValidEmail(email)) {
      setFormError(t('settings.addUserInvalidEmail'));
      return;
    }
    if (password.length < 6) {
      setFormError(t('settings.addUserWeakPassword'));
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t('settings.addUserPasswordMismatch'));
      return;
    }
    setFormError(null);
    await onSave({
      email: email.trim(),
      password,
      role,
      permissions
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-user-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <Card className="max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6 shadow-lg">
        <h2 id="add-user-dialog-title" className="text-lg font-semibold text-[var(--fv-text)]">
          {t('settings.addUserTitle')}
        </h2>
        <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('settings.addUserHint')}</p>

        <div className="mt-5 space-y-4">
          <div>
            <Label htmlFor="add-user-email">{t('auth.email')}</Label>
            <Input
              id="add-user-email"
              type="email"
              className="mt-1"
              autoComplete="off"
              value={email}
              disabled={!online || busy}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="add-user-password">{t('auth.password')}</Label>
              <Input
                id="add-user-password"
                type="password"
                className="mt-1"
                autoComplete="new-password"
                value={password}
                disabled={!online || busy}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="add-user-confirm-password">{t('settings.addUserConfirmPassword')}</Label>
              <Input
                id="add-user-confirm-password"
                type="password"
                className="mt-1"
                autoComplete="new-password"
                value={confirmPassword}
                disabled={!online || busy}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="add-user-role">{t('settings.addUserRole')}</Label>
            <Select
              className="mt-1 max-w-md"
              value={role}
              disabled={!online || busy}
              aria-label={t('settings.addUserRole')}
              onValueChange={(v) => setRole(v as OrgRole)}
              options={MEMBER_ROLES.map((r) => ({
                value: r,
                label: t(`settings.roles.${r}`)
              }))}
            />
            <p className="mt-1 text-xs text-[var(--fv-text-muted)]">{t(roleHintKey(role))}</p>
          </div>

          <div className="rounded-[var(--fv-radius)] border border-[var(--fv-border)] bg-[var(--fv-bg)]/50 p-3">
            <MemberPermissionsEditor
              variant="draft"
              role={role}
              online={online}
              busy={busy}
              onChange={setPermissions}
            />
          </div>

          {formError ? (
            <p className="text-sm text-[var(--fv-danger)]" role="alert">
              {formError}
            </p>
          ) : null}
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!online || busy || !email.trim() || !password || !confirmPassword}
            onClick={() => void handleSave()}
          >
            {t('settings.addUserSave')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
